/** The attorney's positions: every call answers the whole set, since removing one renumbers the rest. */

import { delReturning, get, patch, post, put } from "@/services/api-client";
import type {
  ElementRoleRequest,
  GapAcknowledgementRequest,
  PositionRequest,
  PositionsResponse,
  SuggestionStateResponse,
  VerdictOverrideRequest,
} from "@patent-studio/api-types";

export function listPositions(taskId: string): Promise<PositionsResponse> {
  return get<PositionsResponse>(`/tasks/${taskId}/positions`);
}

export function addPosition(
  taskId: string,
  body: PositionRequest,
): Promise<PositionsResponse> {
  return post<PositionsResponse>(`/tasks/${taskId}/positions`, body);
}

export function amendPosition(
  taskId: string,
  positionId: string,
  body: PositionRequest,
): Promise<PositionsResponse> {
  return patch<PositionsResponse>(
    `/tasks/${taskId}/positions/${positionId}`,
    body,
  );
}

export function confirmPosition(
  taskId: string,
  positionId: string,
): Promise<PositionsResponse> {
  return post<PositionsResponse>(
    `/tasks/${taskId}/positions/${positionId}/confirm`,
  );
}

/**
 * Confirm one position without the documents it was not compared with, and why.
 * The ids are the ones the screen showed; the server refuses them unless they
 * are exactly what is missing now, and refuses a blank reason.
 */
export function acknowledgeGaps(
  taskId: string,
  positionId: string,
  fileIds: string[],
  reason: string,
): Promise<PositionsResponse> {
  const body: GapAcknowledgementRequest = { file_ids: fileIds, reason };
  return put<PositionsResponse>(
    `/tasks/${taskId}/positions/${positionId}/gap-acknowledgement`,
    body,
  );
}

/** Answers the set that is left, renumbered. */
export function removePosition(
  taskId: string,
  positionId: string,
): Promise<PositionsResponse> {
  return delReturning<PositionsResponse>(
    `/tasks/${taskId}/positions/${positionId}`,
  );
}

/** Asks the tool for positions; they land in the set through the worker, and the stream says when. */
export function suggestPositions(
  taskId: string,
): Promise<SuggestionStateResponse> {
  return post<SuggestionStateResponse>(`/tasks/${taskId}/positions/suggest`);
}

/** Marks one element of a position essential or retreat with a reason; `null` takes the mark back. */
export function markElement(
  taskId: string,
  positionId: string,
  elementId: string,
  mark: ElementRoleRequest | null,
): Promise<PositionsResponse> {
  const path = `/tasks/${taskId}/positions/${positionId}/elements/${elementId}/role`;
  return mark === null
    ? delReturning<PositionsResponse>(path)
    : put<PositionsResponse>(path, mark);
}

/** Rules the position's verdict in place of the analysis's; `null` takes the ruling back. */
export function overrideVerdict(
  taskId: string,
  positionId: string,
  verdict: VerdictOverrideRequest["verdict"] | null,
  reason: string | null = null,
): Promise<PositionsResponse> {
  const path = `/tasks/${taskId}/positions/${positionId}/verdict`;
  if (verdict === null) return delReturning<PositionsResponse>(path);
  const body: VerdictOverrideRequest = { verdict, reason };
  return put<PositionsResponse>(path, body);
}
